import { useEffect, type ReactNode } from 'react';

import { Iconos } from './iconos';

/**
 * Primitivas del panel, sobre la escala tonal Material 3 del diseño.
 * Viven en un archivo porque son pequeñas y siempre se usan juntas.
 */

// ── Botón ─────────────────────────────────────────────────────

type Variante = 'primario' | 'tonal' | 'contorno' | 'texto' | 'peligro';

const VARIANTES: Record<Variante, string> = {
  primario: 'bg-primary text-on-primary hover:brightness-110',
  tonal: 'bg-secondary-container text-on-secondary-container hover:brightness-95',
  contorno:
    'border border-outline-variant bg-surface-container-lowest text-on-surface hover:bg-surface-container-high',
  texto: 'text-primary hover:bg-surface-container-high',
  peligro:
    'border border-error-container bg-error-container text-on-error-container hover:brightness-95',
};

export function Boton({
  variante = 'contorno',
  icono,
  className = '',
  children,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variante?: Variante;
  icono?: (p: { tam?: number }) => ReactNode;
}) {
  return (
    <button
      // `button` y no el `submit` que el navegador asume por omisión.
      //
      // Un botón sin `type` dentro de un formulario lo envía al pulsarlo. Con
      // este componente eso significaba que «Quitar» o «Agregar otro» —que solo
      // tocan una lista— mandaban el alta a medio llenar. Quien quiera enviar lo
      // pide con `type="submit"`, que es explícito y se lee.
      type="button"
      {...props}
      className={`toque inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-label-bold transition-all disabled:opacity-40 ${VARIANTES[variante]} ${className}`}
    >
      {icono?.({ tam: 20 })}
      {children}
    </button>
  );
}

// ── Campos de formulario ──────────────────────────────────────

export function Campo({
  etiqueta,
  error,
  ayuda,
  children,
}: {
  etiqueta: string;
  error?: string;
  ayuda?: string;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-label-bold text-on-surface-variant">{etiqueta}</span>
      {children}
      {ayuda && !error && (
        <span className="mt-1 block text-label-sm text-on-surface-variant">{ayuda}</span>
      )}
      {error && (
        <span role="alert" className="mt-1 block text-label-sm text-error">
          {error}
        </span>
      )}
    </label>
  );
}

const CLASE_ENTRADA =
  'toque mt-1 w-full rounded-lg border border-outline-variant bg-surface-container-lowest px-4 py-2 text-body-md text-on-surface outline-none transition-colors placeholder:text-outline focus:border-primary';

export function Entrada(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={CLASE_ENTRADA} />;
}

export function Seleccion(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={CLASE_ENTRADA} />;
}

/** Buscador con su icono dentro, como en el diseño. */
export function Buscador(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <div className="relative">
      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-outline">
        <Iconos.buscar tam={20} />
      </span>
      <input
        {...props}
        type="search"
        className="toque w-full rounded-lg border border-outline-variant bg-surface-container-lowest py-2 pl-10 pr-4 text-body-md text-on-surface outline-none transition-colors placeholder:text-outline focus:border-primary"
      />
    </div>
  );
}

// ── Chip de estado ────────────────────────────────────────────

const TONOS = {
  ok: 'bg-success-container text-success',
  aviso: 'bg-tertiary-container/20 text-tertiary',
  malo: 'bg-error-container text-on-error-container',
  neutro: 'bg-surface-container-high text-on-surface-variant',
  activo: 'bg-secondary-container text-on-secondary-container',
} as const;

export function Chip({
  children,
  tono = 'neutro',
  color,
}: {
  children: ReactNode;
  tono?: keyof typeof TONOS;
  /** Color literal, para los tipos de visita que lo traen del padrón. */
  color?: string;
}) {
  if (color) {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-label-sm"
        style={{ backgroundColor: `${color}1f`, color }}
      >
        <span className="size-2 rounded-full" style={{ backgroundColor: color }} />
        {children}
      </span>
    );
  }
  return (
    <span
      className={`inline-flex items-center rounded-full px-3 py-1 text-label-sm ${TONOS[tono]}`}
    >
      {children}
    </span>
  );
}

// ── Modal ─────────────────────────────────────────────────────

export function Modal({
  titulo,
  abierto,
  alCerrar,
  children,
}: {
  titulo: string;
  abierto: boolean;
  alCerrar: () => void;
  children: ReactNode;
}) {
  useEffect(() => {
    if (!abierto) return;
    const alTeclear = (e: KeyboardEvent) => e.key === 'Escape' && alCerrar();
    window.addEventListener('keydown', alTeclear);

    // La página de atrás deja de desplazarse mientras el modal está abierto.
    // Sin esto salen **dos barras pegadas**: la del modal, que sí hace falta
    // cuando el formulario no cabe, y la de la página, que no lleva a ningún
    // lado porque lo que hay debajo está tapado. Se ve como un fallo de
    // dibujado, y la rueda del ratón mueve una u otra según dónde esté el
    // puntero.
    const { overflow, paddingRight } = document.body.style;
    // Al esconder la barra el contenido se ensancha justo su ancho y la
    // pantalla da un brinco al abrir. Se compensa con relleno.
    const anchoBarra = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (anchoBarra > 0) document.body.style.paddingRight = `${anchoBarra}px`;

    return () => {
      window.removeEventListener('keydown', alTeclear);
      document.body.style.overflow = overflow;
      document.body.style.paddingRight = paddingRight;
    };
  }, [abierto, alCerrar]);

  if (!abierto) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl border border-outline-variant bg-surface-container-lowest p-6"
      >
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-headline-md text-on-surface">{titulo}</h2>
          <button
            onClick={alCerrar}
            aria-label="Cerrar"
            className="flex h-10 w-10 items-center justify-center rounded-full text-on-surface-variant transition-colors hover:bg-surface-container-high"
          >
            <Iconos.cerrar />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── Estados de una tabla ──────────────────────────────────────

export function Cargando({ children = 'Cargando…' }: { children?: ReactNode }) {
  return <p className="p-10 text-center text-body-md text-outline">{children}</p>;
}

export function Vacio({ children }: { children: ReactNode }) {
  return <p className="p-10 text-center text-body-md text-on-surface-variant">{children}</p>;
}

export function ErrorCaja({ children }: { children: ReactNode }) {
  return (
    <p
      role="alert"
      className="m-4 flex items-start gap-2 rounded-lg bg-error-container px-4 py-3 text-body-md text-on-error-container"
    >
      <Iconos.alerta tam={20} className="mt-0.5 shrink-0" />
      <span>{children}</span>
    </p>
  );
}

// ── Superficies ───────────────────────────────────────────────

export function Panel({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={`rounded-xl border border-outline-variant bg-surface-container-lowest ${className}`}
    >
      {children}
    </div>
  );
}

/** Tarjeta de métrica del diseño: etiqueta arriba, cifra grande, icono en burbuja. */
export function Metrica({
  etiqueta,
  valor,
  nota,
  icono,
  tonoIcono = 'text-primary bg-primary-container/20',
}: {
  etiqueta: string;
  valor: ReactNode;
  nota?: string;
  icono: (p: { tam?: number }) => ReactNode;
  tonoIcono?: string;
}) {
  return (
    <div className="flex h-40 flex-col justify-between rounded-xl border border-outline-variant bg-surface-container-lowest p-6">
      <div className="flex items-start justify-between">
        <span className="text-label-bold uppercase tracking-wider text-on-surface-variant">
          {etiqueta}
        </span>
        <span className={`rounded-full p-2 ${tonoIcono}`}>{icono({ tam: 24 })}</span>
      </div>
      <div className="flex items-end gap-3">
        <span className="text-display-unit text-on-surface tabular-nums">{valor}</span>
        {nota && <span className="mb-3 text-label-sm text-on-surface-variant">{nota}</span>}
      </div>
    </div>
  );
}

// ── Tabla ─────────────────────────────────────────────────────

export function Tabla({ cabeceras, children }: { cabeceras: string[]; children: ReactNode }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left">
        <thead className="border-b border-outline-variant">
          <tr>
            {cabeceras.map((c) => (
              <th
                key={c}
                scope="col"
                className="px-6 py-4 text-label-sm uppercase tracking-wider text-on-surface-variant"
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-outline-variant">{children}</tbody>
      </table>
    </div>
  );
}

export function Fila({ children }: { children: ReactNode }) {
  return <tr className="transition-colors hover:bg-surface-container-low">{children}</tr>;
}

export function Celda({
  children,
  fuerte,
  mono,
}: {
  children: ReactNode;
  fuerte?: boolean;
  mono?: boolean;
}) {
  return (
    <td
      className={`px-6 py-4 ${mono ? 'font-mono text-label-bold' : 'text-body-md'} ${
        fuerte ? 'text-on-surface' : 'text-on-surface-variant'
      }`}
    >
      {children}
    </td>
  );
}

export function Paginador({
  pagina,
  paginas,
  total,
  alCambiar,
}: {
  pagina: number;
  paginas: number;
  total: number;
  alCambiar: (p: number) => void;
}) {
  if (total === 0) return null;

  return (
    <div className="flex items-center justify-between border-t border-outline-variant px-6 py-4">
      <span className="text-label-sm text-on-surface-variant">
        {total} registro{total === 1 ? '' : 's'}
      </span>
      <div className="flex items-center gap-2">
        <Boton
          variante="texto"
          disabled={pagina <= 1}
          onClick={() => alCambiar(pagina - 1)}
          icono={Iconos.anterior}
          aria-label="Página anterior"
        />
        <span className="text-label-bold text-on-surface">
          {pagina} de {paginas}
        </span>
        <Boton
          variante="texto"
          disabled={pagina >= paginas}
          onClick={() => alCambiar(pagina + 1)}
          icono={Iconos.siguiente}
          aria-label="Página siguiente"
        />
      </div>
    </div>
  );
}

export function Encabezado({
  titulo,
  descripcion,
  children,
}: {
  titulo: string;
  descripcion?: string;
  children?: ReactNode;
}) {
  return (
    <header className="mb-stack-lg flex flex-col justify-between gap-stack-md md:flex-row md:items-center">
      <div>
        <h1 className="text-headline-lg text-on-surface">{titulo}</h1>
        {descripcion && <p className="mt-1 text-body-md text-on-surface-variant">{descripcion}</p>}
      </div>
      <div className="flex items-center gap-stack-sm">{children}</div>
    </header>
  );
}
